"use client";

import { useEffect, useRef, useState } from "react";
import cn from "@/utils/cn";
import TextComponent from "@/app/_components/TextComponent";

type StatsSectionProps = {
    targetNumber?: number;
    suffix?: string;
    label?: string;
    heading?: string;
    description?: string;
} & React.HTMLAttributes<HTMLDivElement>;

/* ---------------------------------------------
   Count Up Hook
---------------------------------------------- */
function useCountUp(
    targetNumber: number,
    duration: number = 2000,
    isInView: boolean
) {
    // Start at the target so SSR, crawlers, and any non-scrolling load show the
    // real number (e.g. "2400+") instead of a misleading "0+".
    const [count, setCount] = useState(targetNumber);

    useEffect(() => {
        if (!isInView) return;

        // Respect reduced-motion: keep the final number, skip the count-up.
        if (
            typeof window !== "undefined" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            setCount(targetNumber);
            return;
        }

        let startTime: number | null = null;
        let rafId: number;

        // Animate up from zero for users who scroll the section into view.
        setCount(0);

        const animate = (time: number) => {
            if (!startTime) startTime = time;
            const progress = Math.min((time - startTime) / duration, 1);

            // Smooth ease-out
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            setCount(Math.floor(easeOutQuart * targetNumber));

            if (progress < 1) {
                rafId = requestAnimationFrame(animate);
            }
        };

        rafId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafId);
    }, [targetNumber, duration, isInView]);

    return count;
}

/* ---------------------------------------------
   Component
---------------------------------------------- */
export default function StatsSection({
    targetNumber = 2400,
    suffix = "+",
    label = "Audits Generated",
    heading = "Run a free email marketing audit.",
    description = "No more guesswork. Just clean data, clear visuals, and actionable insight.",
    className,
    ...props
}: StatsSectionProps) {
    const sectionRef = useRef<HTMLDivElement>(null);
    const [isInView, setIsInView] = useState(false);

    /* Observe section */
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const animatedCount = useCountUp(targetNumber, 4000, isInView);

    const numberStyles = {
        fontFamily: "Poppins, sans-serif",
        fontWeight: 600,
        fontSize: "clamp(80px, 15vw, 144px)",
        lineHeight: "100%",
        letterSpacing: "0.02em",
        color: "#336DFF",
    };

    return (
        <div
            ref={sectionRef}
            className={cn("relative bg-white py-16 md:py-24 lg:py-32", className)}
            {...props}
        >
            <div className="mx-auto max-w-4xl px-6 text-center">

                {/* Number + Label Group */}
                <div className="inline-block mb-10">

                    {/* Number Wrapper - Relative to maintain width */}
                    <div className="relative">
                        {/* Invisible Spacer - Sets dimensions based on final value */}
                        <span
                            className="invisible block font-poppins"
                            style={numberStyles}
                            aria-hidden="true"
                        >
                            {targetNumber.toLocaleString()}
                            {suffix}
                        </span>

                        {/* Visible Animated Number - Positioned absolute */}
                        <span
                            className="absolute inset-0 flex items-center justify-center font-poppins"
                            style={numberStyles}
                        >
                            {animatedCount.toLocaleString()}
                            {suffix}
                        </span>
                    </div>

                    {/* Label */}
                    <div className="text-right mt-2">
                        <TextComponent
                            type="h2"
                            className="font-semibold"
                            style={{ color: "#336DFF" }}
                        >
                            {label}
                        </TextComponent>
                    </div>

                </div>

                {/* Heading — token class (not inline style) so it flips in dark:
                    kd-secondary-1 is #05004E in light and white in dark. */}
                <TextComponent
                    type="h2"
                    className="font-semibold mb-3 text-kd-secondary-1"
                >
                    {heading}
                </TextComponent>

                {/* Description — kd-secondary-2 is #444A6D in light, #E0E1E5 in dark. */}
                <TextComponent
                    type="h3"
                    className="font-medium max-w-2xl mx-auto text-kd-secondary-2"
                >
                    {description}
                </TextComponent>

            </div>
        </div>
    );
}
